// The Queue › Recently added › Review (Phase 6.3 Part 4, step 1; agreed with Kristi Oct 29–30,
// mocked up in chat Oct 30). New books and Suggested in a fix; the lookup chips come with step 2.
// No database calls here, so the rules can be tested on their own (tests/logic/review.test.ts).

import type { ShelfBook } from '../shelves/model';

/** One waiting suggestion: a value Claude wrote, marked "suggested" (claude_changes). */
export interface Suggestion {
  id: string;
  bookId: string;
  field: string;
  value: unknown;
  /** What the book had before Claude's change ("was Study"). */
  before?: unknown;
  batchId: string;
  batchName: string;
  batchKind: string;
  /** When the batch ran (YYYY-MM-DD). */
  batchOn: string;
}

/** A rule's suggestions, for Suggested in a fix: one or more batches with the same rule name. */
export interface FixGroup {
  key: string;
  name: string;
  on: string;
  batchIds: string[];
  /** "Read type → Demanding", or the fields when the values differ. */
  what: string;
  bookIds: string[];
}

export const FIELD_NAMES: Record<string, string> = {
  fiction: 'Literary genre',
  read_type: 'Read type',
  genres: 'Genres',
  subgenres: 'Subgenres',
  tags: 'Tags',
  series: 'Series',
  asin: 'Kindle ID',
  audible_id: 'Audible ID',
};

const LITERARY: Record<string, string> = { fiction: 'Fiction', nonfiction: 'Nonfiction', poetry: 'Poetry', drama: 'Drama' };

/** A suggested value in words. */
export function valueText(field: string, v: unknown): string {
  if (v === null || v === undefined) return 'none';
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' && x ? String((x as { name?: unknown }).name ?? '') : String(x))).join(', ') : 'none';
  if (field === 'fiction' && typeof v === 'string') return LITERARY[v] ?? v;
  if (typeof v === 'object') {
    const s = v as { name?: unknown; number?: unknown };
    return [s.name, s.number ? `Book ${s.number}` : null].filter(Boolean).join(' · ');
  }
  return String(v);
}

/**
 * The rule a fix batch was made for, from its name: "Nonfiction reads as Demanding, not Study
 * (Kristi, Oct 28): No status" → "Nonfiction reads as Demanding, not Study". Batches of one rule
 * (run shelf by shelf) group together.
 */
export function ruleName(batchName: string): string {
  return (
    batchName
      .replace(/\s*\([^)]*\)/g, '')
      .replace(/:.*$/, '')
      .replace(/,?\s*as suggestions\s*$/i, '')
      .trim() || batchName
  );
}

/** Suggested in a fix: every batch that isn't the nightly new-books task, grouped by rule, newest first. */
export function fixGroups(all: Suggestion[]): FixGroup[] {
  const groups = new Map<string, { name: string; on: string; batches: Set<string>; books: Set<string>; byField: Map<string, Set<string>> }>();
  for (const s of all) {
    if (s.batchKind === 'nightly_new_books') continue;
    const name = ruleName(s.batchName);
    const g = groups.get(name) ?? { name, on: s.batchOn, batches: new Set(), books: new Set(), byField: new Map() };
    g.on = s.batchOn > g.on ? s.batchOn : g.on;
    g.batches.add(s.batchId);
    g.books.add(s.bookId);
    const vals = g.byField.get(s.field) ?? new Set<string>();
    vals.add(valueText(s.field, s.value));
    g.byField.set(s.field, vals);
    groups.set(name, g);
  }
  return [...groups.values()]
    .map((g) => {
      const fields = [...g.byField.entries()];
      const what =
        fields.length === 1 && fields[0]![1].size === 1
          ? `${FIELD_NAMES[fields[0]![0]] ?? fields[0]![0]} → ${[...fields[0]![1]][0]}`
          : fields.map(([f]) => FIELD_NAMES[f] ?? f).join(', ');
      return { key: g.name, name: g.name, on: g.on, batchIds: [...g.batches], what, bookIds: [...g.books] };
    })
    .sort((a, b) => b.on.localeCompare(a.on) || a.name.localeCompare(b.name));
}

/**
 * New books (Kristi, Oct 29): added by hand, from Discover or by Claude (Claude's adds are "by
 * hand" too) and not yet reviewed, newest added first. Imports have their own way in (From import).
 */
export function newBooks<B extends Pick<ShelfBook, 'addedHow' | 'lastReviewedAt' | 'addedAt' | 'importBatch'>>(books: B[], typedLists: Set<string> = new Set()): B[] {
  // Books from a typed or pasted list count too (Kristi, Oct 31): small and hand-made, like Add a
  // book; the sheet import and big file imports stay out.
  return books
    .filter((b) => (b.addedHow === 'by_hand' || b.addedHow === 'discover' || (b.addedHow === 'import' && Boolean(b.importBatch) && typedLists.has(b.importBatch!))) && !b.lastReviewedAt)
    .sort((a, b) => b.addedAt.localeCompare(a.addedAt));
}

// ---------- one book's review ----------

export type Where = 'keep' | 'up_next' | 'future_reads' | 'reference';

/** What the card shows and you can change. */
export interface Draft {
  literary: string | null;
  audience: string | null;
  genres: string[];
  subgenres: string[];
  readType: string | null;
  tags: string[];
  interest: number | null;
  where: Where;
}

/** The book's suggestion fields as the card names them. */
export const CARD_FIELD: Record<string, keyof Draft> = { fiction: 'literary', read_type: 'readType', genres: 'genres', subgenres: 'subgenres', tags: 'tags' };

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What Save does (Kristi, Oct 30): the changes to make, in order (literary genre before genres,
 * genres before subgenres, the move last), and the suggestion marks: a suggested field you left
 * as it is is accepted; one you changed is replaced. Suggestions for fields the card doesn't edit
 * (series, Kindle and Audible IDs) are accepted unless you leave them out.
 */
export function reviewPlan(before: Draft, after: Draft, suggested: string[], leaveOut: string[] = []): { changes: [keyof Draft, unknown][]; accept: string[]; replaced: string[] } {
  const order: (keyof Draft)[] = ['literary', 'audience', 'genres', 'subgenres', 'readType', 'tags', 'interest', 'where'];
  const changes: [keyof Draft, unknown][] = [];
  for (const k of order) {
    if (k === 'where') {
      if (after.where !== 'keep') changes.push(['where', after.where]);
    } else if (!same(before[k], after[k])) changes.push([k, after[k]]);
  }
  const accept: string[] = [];
  const replaced: string[] = [];
  for (const f of suggested) {
    if (leaveOut.includes(f)) continue;
    const k = CARD_FIELD[f];
    if (k && !same(before[k], after[k])) replaced.push(f);
    else accept.push(f);
  }
  return { changes, accept, replaced };
}

/** Where it goes: the book's own shelf first ("Keep in Future reads"); a book with no status starts on Future reads. */
export function whereChoices(status: string | null): { key: Where; name: string }[] {
  const names: Record<string, string> = { up_next: 'Up Next', future_reads: 'Future reads', reference: 'Reference' };
  const out: { key: Where; name: string }[] = [];
  if (status && status !== 'currently_reading' && status !== 'finished' && status !== 'not_for_me') {
    out.push({ key: 'keep', name: `Keep in ${names[status] ?? status}` });
  } else if (status === 'currently_reading' || status === 'finished') {
    // A book being read or finished moves from its own page (Stop reading, Start a re-read).
    return [{ key: 'keep', name: `Keep in ${status === 'finished' ? 'Finished' : 'Currently Reading'}` }];
  } else if (status) {
    out.push({ key: 'keep', name: 'Keep in Not for me' });
  } else out.push({ key: 'keep', name: 'Leave in Recently added' });
  for (const k of ['up_next', 'future_reads', 'reference'] as const) if (k !== status) out.push({ key: k, name: names[k]! });
  return out;
}

/** A book with no status starts on Future reads (board 25); any other book stays where it is. */
export const startWhere = (status: string | null): Where => (status === null ? 'future_reads' : 'keep');

// ---------- sorting Review's lists (consistency pass R6, Kristi Nov 2) ----------

/**
 * A Review list sorted by one of its headers. Each key's value carries its natural direction (newest
 * or highest first where that's what you'd want); `reversed` flips it. Books with no value go last
 * either way, and ties keep the list's own order.
 */
export function sortList<T>(items: T[], val: (t: T) => string | number | null, reversed: boolean): T[] {
  const dir = reversed ? -1 : 1;
  return items
    .map((t, i) => ({ t, i, v: val(t) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) return a.v === b.v ? a.i - b.i : a.v === null ? 1 : -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : String(a.v).localeCompare(String(b.v));
      return c ? c * dir : a.i - b.i;
    })
    .map((x) => x.t);
}

/**
 * A header tapped on a list that opens in its own order (a fix's list): ▲, then ▼, then back to
 * the list's own order (null), so that order is never lost.
 */
export function cycleSort<K extends string>(sort: { key: K; reversed: boolean } | null, k: K): { key: K; reversed: boolean } | null {
  if (!sort || sort.key !== k) return { key: k, reversed: false };
  return sort.reversed ? null : { key: k, reversed: true };
}
